import { useMutation, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useDispatch } from 'react-redux';

import {
  RentalManagerPropertyConditionWebApiHandler_addToPropertyConditionWaitlist,
  RentalManagerPropertyConditionWebApiHandler_getIsOnPropertyConditionWaitlist,
} from '@zg-rentals/rental-manager-env';
import { RENTAL_MANAGER_API_V1_PREFIX } from '../../constants/RouteConstants';
import makeRequest from '../../utils/makeRequestUtils';
import useRequestConfig from '../../hooks/useRequestConfig';

import type { PropertyConditionReportDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { MakeRequestError } from '../../utils/makeRequestUtils';

const IS_ON_PROPERTY_CONDITION_WAITLIST = 'isOnPropertyConditionWaitlist';

export const useGetPropertyConditionsSummary = (propertyId = '', queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/propertyConditions`,
      requestConfig,
      config: {
        params: { propertyId },
      },
    });

  return useQuery<
    {
      title: string;
      actions: Array<{
        text: string;
        url: string;
      }>;
      description: string;
      propertyConditions: Array<PropertyConditionReportDto>;
    },
    MakeRequestError
  >({
    queryKey: ['getPropertyConditionsSummary', propertyId],
    queryFn,
    ...queryOptions,
    enabled: Boolean(propertyId),
    staleTime: 8 * 1000,
  });
};

export const useIsOnPropertyConditionWaitlistQuery = (listingAlias: string) => {
  return useQuery({
    queryKey: [IS_ON_PROPERTY_CONDITION_WAITLIST, { listingAlias }],
    queryFn: () => RentalManagerPropertyConditionWebApiHandler_getIsOnPropertyConditionWaitlist({ listingAlias }),
    select: (data) => data?.isOnWaitlist,
    enabled: Boolean(listingAlias),
  });
};

export const useAddToPropertyConditionWaitlistMutation = (listingAlias: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => RentalManagerPropertyConditionWebApiHandler_addToPropertyConditionWaitlist({ listingAlias }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [IS_ON_PROPERTY_CONDITION_WAITLIST, { listingAlias }] });
    },
  });
};
